import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Image,
  Spinner,
  Alert,
  Button,
  Collapse,
  Badge,
  Modal,
  ProgressBar,
} from "react-bootstrap";
import rankingSvg from "../assets/ranking.svg";
import "../common.css";
import {
  getListByCreated,
  getRankingsByCreated,
  patchList,
  pushRankings,
  getListNarrations,
  generateBookNarration,
} from "../api";
import {
  BookListAccordion,
  RankersAccordion,
} from "../components/AccordionComponent";
import Ranker from "../components/Ranker";
import Footer from "../components/Footer";
import NarrationWaveform from "../components/NarrationWaveform";
import { aggregateRankings } from "../utils";
import Loading from "../components/Loading";
import { RxDotsVertical } from "react-icons/rx";
import { RiArrowRightLine } from "react-icons/ri";

function shuffleBooks(bookList) {
  const shuffledBookList = bookList.slice();

  for (let index = shuffledBookList.length - 1; index > 0; index--) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffledBookList[index], shuffledBookList[randomIndex]] = [
      shuffledBookList[randomIndex],
      shuffledBookList[index],
    ];
  }

  return shuffledBookList;
}

function getSavedRankingsKey(created) {
  return `${created}:rankings`;
}

function getSavedUserKey(created) {
  return `${created}:user`;
}

function getSavedRankings(created) {
  const storedValue = localStorage.getItem(getSavedRankingsKey(created));

  if (!storedValue) {
    return null;
  }

  try {
    return JSON.parse(storedValue);
  } catch {
    return null;
  }
}

function getSavedUser(created) {
  return localStorage.getItem(getSavedUserKey(created)) || "";
}

function List() {
  const [list, setList] = useState({});
  const [currRankings, setCurrRankings] = useState([]);
  const [newRankings, setNewRankings] = useState({});
  const [user, setUser] = useState("");
  const [showRankingsAlert, setShowRankingsAlert] = useState(false);
  const [showScoresAlert, setShowScoresAlert] = useState(false);
  const [showScores, setShowScores] = useState(false);
  const [closing, setClosing] = useState(false);
  const [loadingImg, setLoadingImg] = useState(true);
  const [loadingListData, setLoadingListData] = useState(true);
  const [loadingRankingsData, setLoadingRankingsData] = useState(true);
  const [audioUrls, setAudioUrls] = useState({});
  const [checkingNarrations, setCheckingNarrations] = useState(true);
  const [showNarrationModal, setShowNarrationModal] = useState(false);
  const [narrationStep, setNarrationStep] = useState("intro");
  const [generatingNarrations, setGeneratingNarrations] = useState(false);
  const [generationError, setGenerationError] = useState("");
  const [currentNarrationBook, setCurrentNarrationBook] = useState("");
  const generationRef = useRef(null);
  const navigate = useNavigate();
  const { created } = useParams();
  const savedRankings = useMemo(() => getSavedRankings(created), [created]);
  const savedUser = useMemo(() => getSavedUser(created), [created]);

  function alreadyRanked() {
    const storedValue = localStorage.getItem(created);
    return storedValue === "true";
  }

  const randomizedBookList = useMemo(() => {
    if (!list.book_list) {
      return [];
    }

    return shuffleBooks(list.book_list);
  }, [list.book_list]);

  function bookListWithScore(bookList) {
    if (currRankings.length === 0) {
      return bookList.map((book) => {
        return {
          ...book,
          score: 0,
        };
      });
    }

    const aggregatedRankings = aggregateRankings(
      currRankings.map((r) => r.rankings),
    );

    const rankingOrder = aggregatedRankings.map((r) => r.option);

    const orderMap = new Map();
    rankingOrder.forEach((item, index) => {
      orderMap.set(item, index);
    });

    const sortedBookList = bookList.slice().sort((a, b) => {
      return orderMap.get(a.title) - orderMap.get(b.title);
    });

    return sortedBookList.map((book, index) => {
      return {
        ...book,
        score: aggregatedRankings[index]["points"],
      };
    });
  }

  const displayedBookList = list.closed
    ? bookListWithScore(list.book_list)
    : randomizedBookList;

  useEffect(() => {
    const handleApiCalls = async () => {
      try {
        setLoadingRankingsData(true);
        const fetchedRankings = await getRankingsByCreated(created);
        setCurrRankings(fetchedRankings);
      } catch (error) {
        console.error("Error fetching rankings:", error);
      } finally {
        setLoadingRankingsData(false);
      }
    };
    handleApiCalls();
  }, [created]);

  useEffect(() => {
    let cancelled = false;
    setList({});
    setAudioUrls({});
    setCheckingNarrations(true);
    setShowNarrationModal(false);
    setNarrationStep("intro");
    setGeneratingNarrations(false);
    const handleApiCalls = async () => {
      try {
        setLoadingListData(true);
        const fetchedList = await getListByCreated(created);
        if (cancelled) return;
        if (!fetchedList) throw new Error("List not found.");
        setList({
          ...fetchedList,
          book_list: (fetchedList.book_list || []).map((book, index) => ({
            ...book,
            narrationId: String(book.id || `book-${index}`),
          })),
        });
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching list:", error);
          setCheckingNarrations(false);
        }
      } finally {
        if (!cancelled) setLoadingListData(false);
      }
    };
    handleApiCalls();
    return () => {
      cancelled = true;
    };
  }, [created]);

  useEffect(() => {
    if (!list.id) return;
    const controller = new AbortController();
    setCheckingNarrations(true);
    getListNarrations(list.id, controller.signal)
      .then((urls) => {
        if (!controller.signal.aborted) {
          setAudioUrls(urls);
        }
      })
      .catch(() => {
        // Optional audio must not interrupt browsing. Generation rechecks
        // Storage and displays any errors only after the user requests it.
      })
      .finally(() => {
        if (!controller.signal.aborted) setCheckingNarrations(false);
      });
    return () => controller.abort();
  }, [list.id]);

  useEffect(() => {
    return () => {
      generationRef.current?.abort();
      generationRef.current = null;
    };
  }, [created]);

  useEffect(() => {
    if (!generatingNarrations) return;
    const preventClosing = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", preventClosing);
    return () => window.removeEventListener("beforeunload", preventClosing);
  }, [generatingNarrations]);

  const narrationBooks = list.book_list || [];
  const readyNarrations = narrationBooks.filter(
    (book) => audioUrls[book.narrationId],
  ).length;
  const missingNarrations = narrationBooks.length - readyNarrations;

  const openNarrationModal = () => {
    setNarrationStep("intro");
    setGenerationError("");
    setCurrentNarrationBook("");
    setShowNarrationModal(true);
  };

  const generateNarrations = async () => {
    if (generationRef.current) return;
    const controller = new AbortController();
    generationRef.current = controller;
    setShowNarrationModal(true);
    setNarrationStep("generation");
    setGeneratingNarrations(true);
    setGenerationError("");
    setCurrentNarrationBook("");
    try {
      // Recheck Storage before every attempt, including retries and concurrent
      // generations in another browser. Completed files are the only state.
      let urls = await getListNarrations(list.id, controller.signal);
      if (controller.signal.aborted) return;
      setAudioUrls(urls);
      for (const book of narrationBooks) {
        if (controller.signal.aborted) return;
        if (urls[book.narrationId]) continue;
        setCurrentNarrationBook(book.title);
        const url = await generateBookNarration(
          list.id,
          book.narrationId,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        urls = { ...urls, [book.narrationId]: url };
        setAudioUrls(urls);
      }
      setNarrationStep("complete");
    } catch (error) {
      if (!controller.signal.aborted) setGenerationError(error.message);
    } finally {
      if (generationRef.current === controller) {
        generationRef.current = null;
        setGeneratingNarrations(false);
      }
    }
  };

  function submitRankings() {
    const rankingsObj = { created: created, user: user, rankings: newRankings };

    localStorage.setItem(created, "true");
    localStorage.setItem(getSavedUserKey(created), user);
    localStorage.setItem(
      getSavedRankingsKey(created),
      JSON.stringify(newRankings),
    );
    pushRankings(rankingsObj);
    setNewRankings({});
    setUser("");
    navigate("ranking-submitted");
  }

  function closeRanking() {
    patchList(created, {
      closed: true,
    });
    setNewRankings({});
    setUser("");
    navigate("list-closed");
  }

  const pageImage = (
    <Image
      src={rankingSvg}
      className="main-picture"
      onLoad={() => {
        if (loadingImg) {
          setLoadingImg(false);
        }
      }}
    />
  );

  if (loadingImg) {
    return (
      <>
        <Loading pageImage={pageImage} />
      </>
    );
  }

  if (loadingListData || loadingRankingsData) {
    return (
      <Container fluid>
        <Row className="mt-5">
          <Col
            xs={{ span: 10, offset: 1 }}
            sm={{ span: 8, offset: 2 }}
            md={{ span: 6, offset: 3 }}
          >
            <div className="centered-container">{pageImage}</div>
          </Col>
        </Row>
        <Row className="mt-5">
          <Col
            xs={{ span: 10, offset: 1 }}
            sm={{ span: 8, offset: 2 }}
            md={{ span: 6, offset: 3 }}
          >
            <div className="centered-container">
              <Spinner animation="border" />
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 6, offset: 3 }}
        >
          <div className="centered-container">{pageImage}</div>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 6, offset: 3 }}
        >
          <div className="title">{`View${
            list.closed ? "" : "/Rank"
          } List`}</div>
        </Col>
      </Row>
      <Row className="mt-1">
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 6, offset: 3 }}
        >
          <div className="centered-container">
            <div className="subtitle">{`${
              list.closed ? "Final Standings" : "Books To Rank"
            }`}</div>
            <div>
              <Badge
                className="info-badge"
                onClick={() =>
                  setShowScoresAlert(
                    (prevShowScoreAlert) => !prevShowScoreAlert,
                  )
                }
              >
                i
              </Badge>
            </div>
          </div>
        </Col>
      </Row>
      <Collapse in={showScoresAlert}>
        <Row className="mt-3">
          <Col
            xs={{ span: 10, offset: 1 }}
            sm={{ span: 8, offset: 2 }}
            md={{ span: 6, offset: 3 }}
          >
            <div>
              <Alert className="warning-alert">
                {list.closed ? (
                  <>
                    Each ranking submission assigns to each book as many points
                    as its position in that ranking:
                    <div className="text-centered-container mt-3">
                      <div className="legend-before">
                        1<sup>st</sup> Place
                      </div>
                      <div className="legend-center">
                        <RiArrowRightLine size={20} />
                      </div>
                      <div className="legend-after">1 Point</div>
                    </div>
                    <div className="centered-container mt-2">
                      <RxDotsVertical size={20} />
                    </div>
                    <div className="mt-2 text-centered-container">
                      <div className="legend-before">
                        N<sup>th</sup> Place
                      </div>
                      <div className="legend-center">
                        <RiArrowRightLine size={20} />
                      </div>
                      <div className="legend-after">N Points</div>
                    </div>
                    <div className="mt-3">
                      <b>Lowest score wins.</b>
                    </div>
                    <div className="centered-container mt-3">
                      <Button
                        className="button-primary"
                        onClick={() => {
                          setShowScores((prevShowScores) => !prevShowScores);
                        }}
                      >
                        {showScores ? "Hide" : "Show"} Scores
                      </Button>
                    </div>
                  </>
                ) : (
                  <div>
                    <div>
                      <b>
                        Books are shown in a random order while voting is open,
                        and scores are hidden until this list is closed.
                      </b>
                    </div>
                    <div className="mt-3">
                      This helps keep new rankings from being influenced by
                      partial results.
                    </div>
                  </div>
                )}
              </Alert>
            </div>
          </Col>
        </Row>
      </Collapse>
      <Row className="mt-3">
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 6, offset: 3 }}
        >
          <BookListAccordion
            bookList={displayedBookList}
            deletable={false}
            ranked={true}
            scored={list.closed && showScores}
            audioUrls={audioUrls}
            playbackDisabled={showNarrationModal}
          />
        </Col>
      </Row>
      {list.closed ? (
        <Row className="mt-4">
          <Col
            xs={{ span: 10, offset: 1 }}
            sm={{ span: 8, offset: 2 }}
            md={{ span: 6, offset: 3 }}
          >
            <Alert className="warning-alert">
              It is not possible to add votes to this book list anymore since it
              has been closed.
            </Alert>
          </Col>
        </Row>
      ) : (
        <>
          <Row className="mt-4">
            <Col
              xs={{ span: 10, offset: 1 }}
              sm={{ span: 8, offset: 2 }}
              md={{ span: 6, offset: 3 }}
            >
              <div className="centered-container">
                <Button
                  className="button-tertiary"
                  disabled={closing}
                  onClick={() => setClosing(true)}
                >
                  Close List
                </Button>
              </div>
            </Col>
          </Row>
          <Collapse in={closing}>
            <Row className="mt-4">
              <Col
                xs={{ span: 10, offset: 1 }}
                sm={{ span: 8, offset: 2 }}
                md={{ span: 6, offset: 3 }}
              >
                <div>
                  <Alert className="warning-alert">
                    <div>
                      Closing this list will calculate and reveal the final
                      standings.
                    </div>
                    <div className="mt-3">
                      <b>
                        It will not be possible to submit rankings for this list
                        anymore.
                      </b>
                    </div>
                    <Row className="mt-3">
                      <Col xs={6}>
                        <div className="centered-container">
                          <Button
                            className="button-primary"
                            onClick={closeRanking}
                          >
                            Confirm
                          </Button>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="centered-container">
                          <Button
                            className="button-tertiary"
                            onClick={() => setClosing(false)}
                          >
                            Go Back
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Alert>
                </div>
              </Col>
            </Row>
          </Collapse>
          <Row className="mt-4">
            <Col
              xs={{ span: 10, offset: 1 }}
              sm={{ span: 8, offset: 2 }}
              md={{ span: 6, offset: 3 }}
            >
              <div className="subtitle">Submit Ranking</div>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col
              xs={{ span: 10, offset: 1 }}
              sm={{ span: 8, offset: 2 }}
              md={{ span: 6, offset: 3 }}
            >
              <Ranker
                bookList={randomizedBookList}
                rankings={newRankings}
                setRankings={setNewRankings}
                user={user}
                setUser={setUser}
                submitRankings={submitRankings}
                alreadyRanked={alreadyRanked()}
                savedRankings={savedRankings}
                savedUser={savedUser}
              />
            </Col>
          </Row>
        </>
      )}

      <Row className="mt-4">
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 6, offset: 3 }}
        >
          <div className="centered-container">
            <div className="subtitle">
              {`${currRankings.length} ${
                currRankings.length === 1 ? "Ranking" : "Rankings"
              } Sent`}
            </div>
            {!list.closed && (
              <div>
                <Badge
                  className="info-badge"
                  onClick={() =>
                    setShowRankingsAlert(
                      (prevShowRankingsAlert) => !prevShowRankingsAlert,
                    )
                  }
                >
                  i
                </Badge>
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Collapse in={showRankingsAlert}>
        <Row className="mt-3">
          <Col
            xs={{ span: 10, offset: 1 }}
            sm={{ span: 8, offset: 2 }}
            md={{ span: 6, offset: 3 }}
          >
            <div>
              <Alert className="warning-alert">
                <div>To keep voting unbiased:</div>
                <div className="mt-3">
                  <b>
                    Individual rankings and final scores will only be revealed
                    after this list has been closed.
                  </b>
                </div>
              </Alert>
            </div>
          </Col>
        </Row>
      </Collapse>
      <Row className="mt-3">
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 6, offset: 3 }}
        >
          {(currRankings.length === 0 && (
            <div>
              {list.closed ? (
                <Alert className="info-alert">
                  This book list has no rankings.
                </Alert>
              ) : (
                <Alert className="info-alert">
                  <div>This book list currently has no rankings.</div>
                  <div className="mt-3">
                    When rankings are submitted, voters' names will be shown in
                    this section.
                  </div>
                </Alert>
              )}
            </div>
          )) || (
            <RankersAccordion rankings={currRankings} closed={list.closed} />
          )}
        </Col>
      </Row>
      {list.imageUrl && (
        <>
          <Row className="mt-4">
            <Col
              xs={{ span: 10, offset: 1 }}
              sm={{ span: 8, offset: 2 }}
              md={{ span: 6, offset: 3 }}
            >
              <div className="section-divider"></div>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col
              xs={{ span: 10, offset: 1 }}
              sm={{ span: 8, offset: 2 }}
              md={{ span: 6, offset: 3 }}
            >
              <div className="centered-container">
                <div className="clubbers-photo-frame">
                  <Image src={list.imageUrl} fluid className="clubbers-photo" />
                </div>
              </div>
            </Col>
          </Row>
        </>
      )}
      <Modal
        show={showNarrationModal}
        backdrop="static"
        keyboard={false}
        centered
        className="narration-modal"
        aria-label="Audio descriptions"
        aria-describedby={
          narrationStep === "intro" ? "narration-introduction" : undefined
        }
        onHide={() => {
          if (!generatingNarrations) setShowNarrationModal(false);
        }}
      >
        <Modal.Body>
          {narrationStep === "intro" ? (
            <div id="narration-introduction">
              <p>
                Proceeding will generate <strong>audio versions</strong> of the
                book descriptions, which you can play in addition to reading the
                written descriptions.
              </p>
              <p className="mb-0">
                If you start, please <strong>keep this page open</strong> until
                the generation process has finished.
              </p>
            </div>
          ) : (
            <>
              <p aria-live="polite">
                <strong>
                  {readyNarrations} of {narrationBooks.length}
                </strong>{" "}
                recordings ready
              </p>
              <ProgressBar
                className="narration-progress"
                now={
                  narrationBooks.length
                    ? (readyNarrations / narrationBooks.length) * 100
                    : 0
                }
                aria-label="Narration progress"
                animated={generatingNarrations}
                striped={generatingNarrations}
              />
              {narrationStep === "complete" ? (
                <p className="mt-3 mb-0" role="status">
                  <strong>The audio descriptions are ready.</strong> You can now
                  close this page.
                </p>
              ) : generatingNarrations ? (
                <>
                  <p className="mt-3" aria-live="polite">
                    <Spinner animation="border" size="sm" className="me-2" />
                    {currentNarrationBook
                      ? `Preparing “${currentNarrationBook}”…`
                      : "Checking saved recordings…"}
                  </p>
                  <p className="mb-0">
                    Please <strong>keep this page open</strong> until the
                    generation process has finished.
                  </p>
                </>
              ) : (
                generationError && (
                  <>
                    <Alert className="warning-alert mt-3" role="alert">
                      {currentNarrationBook && (
                        <div className="mb-2">
                          Couldn’t prepare “{currentNarrationBook}”.
                        </div>
                      )}
                      {generationError}
                    </Alert>
                    <p className="mt-3 mb-0">
                      Generation failed. Any completed recordings were saved.{" "}
                      <strong>Use the Retry button</strong> to try again.
                    </p>
                  </>
                )
              )}
            </>
          )}
        </Modal.Body>
        {narrationStep === "intro" ? (
          <Modal.Footer>
            <Button
              className="button-tertiary"
              onClick={() => setShowNarrationModal(false)}
            >
              Go back
            </Button>
            <Button className="button-primary" onClick={generateNarrations}>
              Proceed
            </Button>
          </Modal.Footer>
        ) : narrationStep === "complete" ? (
          <Modal.Footer>
            <Button
              className="button-primary"
              onClick={() => setShowNarrationModal(false)}
            >
              Done
            </Button>
          </Modal.Footer>
        ) : (
          !generatingNarrations &&
          generationError && (
            <Modal.Footer>
              <Button
                className="button-tertiary"
                onClick={() => setShowNarrationModal(false)}
              >
                Close
              </Button>
              <Button className="button-primary" onClick={generateNarrations}>
                Retry
              </Button>
            </Modal.Footer>
          )
        )}
      </Modal>
      <Footer
        actions={
          !checkingNarrations &&
          missingNarrations > 0 && (
            <Button
              className="button-tertiary footer-narration-button"
              aria-label="Audio narrations"
              aria-haspopup="dialog"
              title="Audio narrations"
              onClick={openNarrationModal}
            >
              <NarrationWaveform />
            </Button>
          )
        }
      />
    </Container>
  );
}

export default List;
